import React, { forwardRef, useState, useMemo, useCallback, useRef } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes } from "utils/formUtils";
import GenericMappingForm from "../GenericMappingForm";
import { find, uniqBy } from "lodash";
import { Form } from "antd";

const HCLAppscanCloudMappingForm = forwardRef(({ disableProjectFields = false, ...props },ref)=> {
    const [form] = Form.useForm();

    const [applicationInitialOptions] = useState(() => {
        if (props.record) {
            const { applicationId, applicationName } = props.record || {};
            if (applicationId && applicationName) {
                const ids = String(applicationId).split(';');
                const names = String(applicationName).split(';');
                return ids.map((id, i) => ({ id, name: names[i] }));
            }
        }
        return [];
    });

    const applicationOptionsRef = useRef(applicationInitialOptions);

    const [initialValues] = useState(() => {
        if (props.record) {
            const { applicationId } = props.record || {};
            const selectedApplicationIds = applicationId ? String(applicationId).split(';') : [];
            return [{
                product: props.defaultProduct,
                subProduct: props.defaultSubProduct,
                environment: props.defaultEnvironment,
                applicationId: selectedApplicationIds
            }];
        }
        return [{
            product: props.defaultProduct,
            subProduct: props.defaultSubProduct,
            environment: props.defaultEnvironment,
            applicationId: []
        }];
    });

    const fetchApplications = useCallback((params) => {
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(
            props.toolName, { login_id: props.loginConfigId, ...params }
        );
        promise.then((response) => {
            const projects = response?.projects || [];
            applicationOptionsRef.current = uniqBy([...applicationOptionsRef.current, ...projects], 'id');
        });
        return { promise , cancel };
    }, [props.toolName, props.loginConfigId]);

    const getExtraFieldsData = useCallback((config) => {
        const { applicationId } = config;
        const ids = Array.isArray(applicationId) ? applicationId : (applicationId ? String(applicationId).split(';') : []);
        const getNameById = (id) => {
            const list = applicationOptionsRef.current;
            return (find(list, p => String(p.id) === String(id))?.name) || "";
        }
        const names = ids.map(getNameById).filter(Boolean);

        return {
            applicationId: ids.join(';'),
            applicationName: names.join(';'),
            ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
        };
    }, [applicationInitialOptions]);

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
        const { applicationId } = config || {};
        return {
            applicationId: applicationId ? String(applicationId).split(';') : [],
            ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
        };
    }, []);

    const extraFields = useMemo(() => [{
        name: 'applicationId',
        colSpan: '12',
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        help: "Enter full application name for filtering support",
        label: 'Application Name',
        rules: [{ required: true, message: `Application Name is required` }],
        disabled: disableProjectFields,
        props: {
            api: fetchApplications,
            mode: "multiple",
            allowClear: true,
            autoFocus: !disableProjectFields,
            showSearch: true,
        }
    }], [fetchApplications,disableProjectFields]);

    return (
        <GenericMappingForm
            form={form}
            ref={ref}
            getExtraFieldsData={getExtraFieldsData}
            parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
            extraFields={extraFields}
            formDefaultValues={initialValues}
            {...props}
        />
    )
});

export default HCLAppscanCloudMappingForm;
